<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': expandAll }"
                        v-b-tooltip.hover.bottom :title="expandAll ? 'Contraer grupos' : 'Desplegar todos los grupos'"
                        @click="toggleExpandAll()">
                <AppIcon :name="expandAll ? 'unfold_less' : 'unfold_more'" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn" v-b-tooltip.hover.bottom
                        title="Refrescar" aria-label="Refrescar" @click="filter()">
                <AppIcon name="refresh" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn list-view-icon-btn--primary" v-b-tooltip.hover.bottom
                        title="Agregar instancia" aria-label="Agregar instancia" @click="add()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 320px;">
              <label class="filter-label">Buscar</label>
              <b-form-input type="text" size="sm" placeholder="Nombre" v-model="filters.name" v-on:keyup.enter.native="filter" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 300px;">
              <label class="filter-label">Cliente</label>
              <v-select :options="arr.select.customers" v-model="filters.customer" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">Agrupación</label>
              <v-select :options="arr.select.categories" v-model="filters.categories" placeholder="Todas" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 150px; max-width: 200px;">
              <label class="filter-label">Tipo</label>
              <v-select :options="arr.filter.type" v-model="filters.type" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">VPS instalación</label>
              <v-select :options="arr.select.servers" v-model="filters.instances_server" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <b-button variant="outline-dark" @click="filter()" size="sm" v-b-tooltip.hover title="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <MonitorSummary class="mb-3" :tiles="summaryTiles" :active="quickFilter" @select="quickFilter = $event" />

      <b-card>
        <div class="instances-monitor-toolbar">
          <MonitorGroupBy v-model="groupBy" :options="groupOptions" />
        </div>

        <b-alert v-if="idsFilter.length" show variant="warning" class="d-flex align-items-center justify-content-between py-2">
          <span><AppIcon name="notifications" class="mr-1" /> Mostrando {{idsFilter.length}} instancias sin reportes de Monitor (desde una notificación).</span>
          <b-button size="sm" variant="outline-dark" @click="clearIdsFilter()"><AppIcon name="close" class="mr-1" /> Ver todos</b-button>
        </b-alert>

        <div v-if="table.isBusy" class="text-center py-4" role="status" aria-live="polite">
          <b-spinner variant="primary" label="Cargando instancias"></b-spinner>
        </div>

        <div v-else-if="visibleItems.length">
          <MonitorTree :nodes="tree" :items="visibleItems" :expand-all="expandAll" :force-expand="idsFilter.length > 0" :summarize="getGroupHealth" :reveal-id="revealId" storage-key="tree_open_crud_instances_monitor_system" @manual="expandAll = false">
            <template #health="{ summary }">
                <span v-if="summary.avisos" class="text-warning" v-b-tooltip.hover :title="summary.avisos + ' instancia(s) con avisos de Monitor'">
                  <AppIcon name="warning" /> {{summary.avisos}}
                </span>
                <span v-if="summary.backups" class="text-danger ml-2" v-b-tooltip.hover :title="summary.backups + ' instancia(s) con backups no realizados'">
                  <AppIcon name="backup" /> {{summary.backups}}
                </span>
                <span v-if="summary.attention" class="text-muted ml-2" v-b-tooltip.hover :title="summary.attention + ' instancia(s) sin reportes o con reporte atrasado'">
                  <AppIcon name="schedule" /> {{summary.attention}}
                </span>
                <span v-if="summary.pending" class="text-info ml-2" v-b-tooltip.hover :title="summary.pending + ' instancia(s) pendientes de emparejar en Monitor'">
                  <AppIcon name="link_off" /> {{summary.pending}}
                </span>
            </template>
            <template #leaf="{ items }">
              <b-table class="app-table mb-0 instances-monitor-table"
                       responsive
                       :stacked="isMobileViewport ? 'md' : false"
                       :hover="true"
                       :small="true"
                       :items="items"
                       :fields="tableFields"
                       :tbody-tr-class="rowClass"
                       @row-clicked="openDrawer">

                <template v-slot:cell(name)="data">
                  <div class="instances-monitor__identity">
                    <span class="instances-monitor__avatar" :class="'instances-monitor__avatar--' + data.item.type" v-b-tooltip.hover :title="getTypeLabel(data.item.type)">
                      <img v-if="getTypeImage(data.item.type)" :src="getTypeImage(data.item.type)" alt="" class="instances-monitor__brand" />
                      <AppIcon v-else :name="getTypeIcon(data.item.type)" />
                    </span>
                    <span class="instances-monitor__identity-text">
                      <span>
                        <b>{{data.item.name}}</b>
                        <AppIcon v-if="data.item.intranet" name="lan" class="text-info ml-1" v-b-tooltip.hover title="Con sitio web" />
                      </span>
                      <span v-if="instanceLinks(data.item).length" class="instances-monitor__urls">
                        <a v-for="link in instanceLinks(data.item)" :key="link.url" :href="link.url" target="_blank" rel="noopener noreferrer"
                           class="instances-monitor__url" @click.stop v-b-tooltip.hover="{ title: link.url, boundary: 'window' }">
                          <AppIcon name="open_in_new" /> {{link.label}}
                        </a>
                      </span>
                      <small v-if="data.item.solucion" class="text-muted text-truncate">{{data.item.solucion}}</small>
                    </span>
                  </div>
                </template>

                <template v-slot:cell(customer)="data">
                  <MonitorAvatarName :entity="data.item.customer" />
                </template>

                <template v-slot:cell(service)="data">
                  <span v-if="data.item.service_customer" class="instances-monitor__service instances-monitor__service--ok"
                        v-b-tooltip.hover.left="{ title: 'Servicio: ' + data.item.service_customer.name, boundary: 'window' }">
                    <AppIcon name="rocket_launch" />
                  </span>
                  <span v-else class="instances-monitor__service instances-monitor__service--missing"
                        v-b-tooltip.hover.left="{ title: 'Sin servicio asociado', boundary: 'window' }">
                    <AppIcon name="report" />
                  </span>
                </template>

                <template v-slot:cell(server)="data">
                  <a v-if="data.item.server" href="#" class="instances-monitor__link text-nowrap"
                     @click.prevent.stop="goToServer(data.item.server)">
                    <AppIcon name="dns" /> {{data.item.server.name}}
                  </a>
                  <span v-else class="text-muted" v-b-tooltip.hover title="Monitor informa la VPS al emparejar">Sin asignar</span>
                </template>

                <template v-slot:cell(status)="data">
                  <span v-if="intranetMismatch(data.item)" class="instances-monitor__backup-alert" v-b-tooltip.hover="{ title: intranetMismatch(data.item), boundary: 'window' }">
                    <AppIcon name="warning" /> {{intranetMismatchShort(data.item)}}
                  </span>
                  <MonitorStatus v-if="data.item.monitor_status" :value="data.item.monitor_status" :type="data.item.type" mode="badge" />
                  <span v-else class="text-muted">—</span>
                </template>

                <template v-slot:cell(backups)="data">
                  <span v-if="unexpectedBackups(data.item)" class="instances-monitor__backup-alert" v-b-tooltip.hover="{ title: 'Monitor reporta backups pero la instancia no tiene activado el control de backups', boundary: 'window' }">
                    <AppIcon name="warning" /> No debería tener backups
                  </span>
                  <MonitorBackups v-if="data.item.monitor_backups" :value="data.item.monitor_backups" mode="badge" />
                  <span v-else-if="!data.item.control_backups" class="text-muted">Sin control</span>
                  <span v-else-if="!data.item.monitor_id" class="text-muted">Activado</span>
                  <span v-else class="text-danger" v-b-tooltip.hover title="Backups activados pero Monitor todavía no reportó datos"><AppIcon name="backup" /> Sin reporte</span>
                </template>

                <template v-slot:cell(reported)="data">
                  <MonitorReportChip :item="data.item" />
                </template>

                <template v-slot:cell(avisos)="data">
                  <MonitorAvisos :value="data.item.monitor_avisos" mode="badge" />
                </template>

                <template v-slot:cell(f_action)="data">
                  <b-dropdown :right="!isMobileViewport" no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle"
                              :class="{ 'pull-right': !isMobileViewport }">
                    <template #button-content><AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" /><span v-if="isMobileViewport" class="app-table-action-label">Opciones</span></template>
                    <b-dropdown-item @click="openDrawer(data.item)"><AppIcon name="visibility" /> Ver detalle</b-dropdown-item>
                    <b-dropdown-item v-if="data.item.monitor_url" :href="data.item.monitor_url" target="_blank" rel="noopener noreferrer"><AppIcon name="open_in_new" /> Abrir en Monitor</b-dropdown-item>
                    <b-dropdown-item @click="edit(data.item)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                    <b-dropdown-item v-if="!data.item.monitor_id" @click="remove(data.item)"><AppIcon name="delete" class="text-danger" /> Eliminar</b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>
            </template>
          </MonitorTree>
        </div>
        <EmptyState v-else icon="deployed_code" title="No se encontraron instancias" description="Ajustá los filtros o agregá una instancia nueva." />

        <div class="app-table-footer" v-if="!table.isBusy">
          <span class="list-view-toolbar__count">{{ visibleItems.length }} registros</span>
        </div>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <div>
            <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
              <AppIcon name="arrow_back" class="mr-1" />
              Volver
            </b-button>
          </div>
        </div>
      </b-card>

      <!-- ########################### -->
      <!-- #####     DETALLE     ##### -->
      <!-- ########################### -->
      <b-sidebar id="sidebar-instances-monitor-system"
                 v-model="drawer.active"
                 right
                 shadow
                 backdrop
                 title="Detalle de la instancia"
                 width="520px">
        <div class="list-view-drawer" v-if="selected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">
                <img v-if="getTypeImage(selected.type)" :src="getTypeImage(selected.type)" alt="" class="instances-monitor__brand instances-monitor__brand--sm mr-1" />
                <AppIcon v-else :name="getTypeIcon(selected.type)" class="mr-1" />{{getTypeLabel(selected.type)}}
              </span>
              <div>
                {{selected.name}}
                <AppIcon v-if="selected.intranet" name="lan" class="text-info ml-1" v-b-tooltip.hover title="Con sitio web" />
              </div>
              <div class="list-view-drawer__meta">
                {{selected.customer ? selected.customer.name : 'Sin cliente'}}
                <span v-if="selected.monitor_id"> · Monitor: {{selected.monitor_id}}</span>
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoPrev" v-b-tooltip.hover title="Instancia anterior" @click="goPrev()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoNext" v-b-tooltip.hover title="Instancia siguiente" @click="goNext()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat instances-monitor__stat--wide">
              <span class="list-view-drawer__stat-label">Monitor</span>
              <span class="list-view-drawer__stat-value"><MonitorReportChip :item="selected" /></span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': backupProblem(selected) }">
              <span class="list-view-drawer__stat-label">Backups</span>
              <span class="list-view-drawer__stat-value">
                <AppIcon name="backup" class="mr-1" />{{backupLabel(selected)}}
              </span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': countAvisos(selected) }">
              <span class="list-view-drawer__stat-label">Avisos</span>
              <span class="list-view-drawer__stat-value"><AppIcon name="warning" class="mr-1" />{{countAvisos(selected)}}</span>
            </div>
          </div>

          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="edit(selected)"><AppIcon name="edit" class="mr-1" /> Editar</b-button>
            <b-button v-if="selected.monitor_url" size="sm" variant="outline-secondary" :href="selected.monitor_url" target="_blank" rel="noopener noreferrer">
              <AppIcon name="open_in_new" class="mr-1" /> Abrir en Monitor
            </b-button>
          </div>

          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="badge" /> Datos</div>
            <div class="monitor-detail">
              <div class="monitor-detail__row">
                <span class="monitor-detail__label"><AppIcon name="person" /> Cliente</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.customer">{{selected.customer.name}}</span>
                  <span v-else class="monitor-detail__empty">—</span>
                  <span v-if="selected.customer && !selected.customer.active" class="monitor-pill monitor-pill--danger">Inactivo</span>
                </span>
              </div>
              <div class="monitor-detail__row">
                <span class="monitor-detail__label"><AppIcon name="folder" /> Agrupación</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.categorie">{{selected.categorie.name}}</span>
                  <span v-else class="monitor-detail__empty">Sin agrupación</span>
                </span>
              </div>
              <div class="monitor-detail__row">
                <span class="monitor-detail__label"><AppIcon name="dns" /> VPS</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.server">{{selected.server.name}}</span>
                  <span v-else class="monitor-detail__empty">Sin asignar (la informa Monitor)</span>
                </span>
              </div>
              <div class="monitor-detail__row">
                <span class="monitor-detail__label"><AppIcon name="lan" /> Sitio web</span>
                <span class="monitor-detail__value">
                  <span class="monitor-pill" :class="selected.intranet ? 'monitor-pill--info' : 'monitor-pill--neutral'">{{selected.intranet ? 'Sí' : 'No'}}</span>
                </span>
              </div>
              <div v-if="parameters.integrateServices" class="monitor-detail__row monitor-detail__row--stack">
                <span class="monitor-detail__label"><AppIcon name="rocket_launch" /> Servicio</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.service_customer">{{selected.service_customer.name}}</span>
                  <span v-else class="monitor-pill monitor-pill--danger">Sin servicio</span>
                </span>
              </div>
              <div class="monitor-detail__row monitor-detail__row--stack">
                <span class="monitor-detail__label"><AppIcon name="lightbulb" /> Solución</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.solucion">{{selected.solucion}}</span>
                  <span v-else class="monitor-detail__empty">Sin cargar</span>
                </span>
              </div>
              <div v-if="selected.observations" class="monitor-detail__row monitor-detail__row--stack">
                <span class="monitor-detail__label"><AppIcon name="notes" /> Observaciones</span>
                <span class="monitor-detail__value">{{selected.observations}}</span>
              </div>
            </div>
          </div>

          <div v-if="!selected.monitor_id">
            <b-alert show variant="info" class="mb-0 py-2">
              <AppIcon name="link_off" class="mr-1" /> Pendiente de emparejar: Monitor todavía no tomó esta instancia.
            </b-alert>
          </div>

          <template v-else>
            <div v-if="selected.monitor_observacion">
              <div class="list-view-drawer__section-title"><AppIcon name="sticky_note_2" /> Observaciones de Monitor</div>
              <div class="monitor-detail">
                <div class="monitor-detail__row monitor-detail__row--stack">
                  <span class="monitor-detail__value">{{selected.monitor_observacion}}</span>
                </div>
              </div>
            </div>

            <div>
              <div class="list-view-drawer__section-title"><AppIcon name="warning" /> Avisos</div>
              <MonitorAvisos :value="selected.monitor_avisos" mode="list" />
            </div>

            <div>
              <div class="list-view-drawer__section-title"><AppIcon name="monitor_heart" /> Estado</div>
              <div v-if="intranetMismatch(selected)" class="alert alert-warning py-2 mb-2">
                <AppIcon name="warning" class="mr-1" /> {{intranetMismatch(selected)}}
              </div>
              <MonitorStatus :value="selected.monitor_status" :type="selected.type" mode="detail" />
            </div>

            <div>
              <div class="list-view-drawer__section-title"><AppIcon name="backup" /> Backups</div>
              <template v-if="!selected.control_backups">
                <div v-if="unexpectedBackups(selected)" class="alert alert-warning py-2 mb-2">
                  <AppIcon name="warning" class="mr-1" /> Monitor reporta backups pero la instancia no tiene activado el control de backups. Revisá si debe controlarlos o si hay que quitar los backups del servidor.
                </div>
                <div v-else class="text-muted">La instancia no controla backups.</div>
              </template>
              <MonitorBackups v-if="selected.control_backups || unexpectedBackups(selected)" :value="selected.monitor_backups" mode="detail" />
              <div v-if="selected.observations_backups" class="monitor-detail mt-2">
                <div class="monitor-detail__row monitor-detail__row--stack">
                  <span class="monitor-detail__label"><AppIcon name="notes" /> Observaciones de backups</span>
                  <span class="monitor-detail__value">{{selected.observations_backups}}</span>
                </div>
              </div>
            </div>
          </template>

          <div v-if="selected.type == 'zyncro'">
            <div class="list-view-drawer__section-title"><AppIcon name="fact_check" /> Auditoría</div>
            <MonitorAudit :value="selected.audit" />
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="selected && !selected.monitor_id">
            <b-button size="sm" variant="outline-danger" block @click="remove(selected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- ALTA / EDICION (datos administrativos) -->
      <b-modal v-model="modal.form.active"
               :key="modal.form._id"
               :id="modal.form._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="deployed_code" /></span>
          <h5 class="app-modal-header__title">{{modal.form.title}}</h5>
        </div>

        <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark" content-class="pt-3">
          <!-- GENERAL -->
          <b-tab>
            <template #title><AppIcon name="description" class="mr-1" /> General</template>
            <div v-if="!crud.form.id" class="instances-monitor-form__hint mb-3">
              <AppIcon name="info" /> Queda <b>pendiente de emparejar</b>: Monitor la instala, la empareja e informa la VPS.
            </div>
            <b-row>
              <b-col md="8">
                <b-form-group label="Nombre">
                  <b-form-input type="text" size="sm" v-model="crud.form.name" required placeholder="Ingresar un nombre" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Tipo" :description="crud.form.id ? 'No se puede cambiar' : ''">
                  <b-form-select size="sm" v-model="crud.form.type" :options="arr.select.type" :disabled="!!crud.form.id" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <FindObject render="search"
                            type="customers"
                            @select-object="loadCustomers($event)"
                            :valueID="crud.form.customer_id"
                            :where="conditionCustomersActive" />
              </b-col>
              <b-col md="6">
                <FindObject tag="Agrupación"
                            render="search"
                            type="instances-categories"
                            @select-object="loadCategories($event)"
                            :valueID="crud.form.categories_id"
                            :where="conditionCategoriesSystem" />
              </b-col>
              <b-col md="12" v-if="parameters.integrateServices">
                <FindObject tag="Servicio"
                            render="search"
                            display="name"
                            type="servicesCustomers"
                            @select-object="loadServicesCustomersDetails($event)"
                            :valueID="crud.form.services_customers_details_id"
                            :where="conditionServicesCustomersDetails"
                            :key="crud.form.customer_id" />
              </b-col>
              <b-col md="12">
                <b-form-group label="Solución" description="Qué resuelve o qué módulos usa el cliente con esta instancia.">
                  <app-textarea v-model="crud.form.solucion" lazy placeholder="Ingresar la solución..." rows="2" max-rows="6" />
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group label="Observaciones" class="mb-0">
                  <app-textarea v-model="crud.form.observations" lazy placeholder="Ingresar las observaciones..." rows="3" max-rows="6" />
                </b-form-group>
              </b-col>
            </b-row>
          </b-tab>

          <!-- INTRANET -->
          <b-tab>
            <template #title><AppIcon name="lan" class="mr-1" /> Sitio web</template>
            <div v-if="crud.form.type != 'zyncro'" class="instances-monitor-form__hint">
              <AppIcon name="info" /> El sitio web solo aplica a instancias <b>Zyncro</b>.
            </div>
            <template v-else>
              <div class="instances-monitor-form__switch">
                <b-form-checkbox v-model="crud.form.intranet" switch>
                  <b>Tiene sitio web</b>
                </b-form-checkbox>
                <small class="text-muted d-block mt-1">
                  Si está activa, Monitor informa en cada reporte el estado y la versión del sitio web, y se muestran en el detalle de la instancia.
                </small>
              </div>
            </template>
          </b-tab>

          <!-- BACKUPS -->
          <b-tab>
            <template #title><AppIcon name="backup" class="mr-1" /> Backups</template>
            <div class="instances-monitor-form__switch">
              <b-form-checkbox v-model="crud.form.control_backups" switch>
                <b>Controlar backups</b>
              </b-form-checkbox>
              <small class="text-muted d-block mt-1">
                Monitor informa el último backup, el próximo y los históricos. Si un backup no se hace, aparece en el indicador "Backups no realizados" y en el widget.
              </small>
            </div>
            <b-form-group v-if="crud.form.control_backups" label="Observaciones de backups" class="mt-3 mb-0">
              <app-textarea v-model="crud.form.observations_backups" lazy placeholder="Ingresar las observaciones de backups..." rows="3" max-rows="6" />
            </b-form-group>
          </b-tab>
        </b-tabs>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="save()"><AppIcon name="save" class="mr-1" /> Guardar</b-button>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Param from '@/config/parameters'
  import Storage from '@/handler/storageLocal'
  import FindObject from '@/components/inc/find/findObject'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import zyncroIcon from '@/assets/branding/zyncro-icon.svg'
  import laravelIcon from '@/assets/branding/laravel-icon.svg'
  import dockerIcon from '@/assets/branding/docker-icon.svg'
  import { reportState, countAvisos, parseJson, parseDate, timeAgo } from './inc/monitorConfig'
  import MonitorAvisos from './inc/monitorAvisos'
  import MonitorStatus from './inc/monitorStatus'
  import MonitorBackups from './inc/monitorBackups'
  import MonitorAudit from './inc/monitorAudit'
  import MonitorReportChip from './inc/monitorReportChip'
  import MonitorSummary from './inc/monitorSummary'
  import MonitorAvatarName from './inc/monitorAvatarName'
  import './inc/monitorDetail.css'
  import MonitorTree from './inc/monitorTree'
  import MonitorGroupBy from './inc/monitorGroupBy'
  import { buildTree, flattenTree } from './inc/monitorTreeBuilder'

  const SYSTEM_TYPES = [
    { value: 'zyncro', text: 'Zyncro', icon: 'deployed_code', image: zyncroIcon },
    { value: 'laravel', text: 'Laravel', icon: 'code', image: laravelIcon },
    { value: 'docker', text: 'Docker', icon: 'view_in_ar', image: dockerIcon },
  ]

  export default {
    components: {
      MonitorAvisos,
      MonitorStatus,
      MonitorBackups,
      MonitorAudit,
      MonitorReportChip,
      MonitorSummary,
      MonitorAvatarName,
      MonitorTree,
      MonitorGroupBy,
      FindObject,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.INSTANCIAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudInstancesMonitorSystem',
          elements: {}
        },
        parameters: {
          integrateServices: Helper.hasParametersAccess(Param.P59),
        },
        table: {
          items: [],
          fields: [],
          isBusy: false,
        },
        isMobileViewport: false,
        mobileMql: null,
        filtersOpen: false,
        expandAll: false,
        groupBy: ['categorie'],
        quickFilter: null,
        // ids que llegan desde una notificación (?ids=1,2,3)
        idsFilter: [],
        // instancia a seleccionar al llegar desde Servidores (params.select)
        revealId: null,
        pendingSelect: null,
        filters: {
          name: '',
          customer: null,
          categories: null,
          type: null,
          instances_server: null,
        },
        crud: {
          form: {
            id: 0,
            name: '',
            type: 'zyncro',
            customer_id: 0,
            categories_id: 0,
            services_customers_details_id: 0,
            observations: '',
            control_backups: false,
            observations_backups: '',
            intranet: false,
            solucion: '',
          },
        },
        modal: {
          form: { active: false, title: '' },
        },
        drawer: {
          active: false,
        },
        selected: null,
        arr: {
          select: {
            customers: [],
            categories: [],
            servers: [],
            type: SYSTEM_TYPES,
          },
          filter: {
            type: SYSTEM_TYPES.map(item => ({ code: item.value, label: item.text })),
          },
        },
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted() {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.restoreFilterStorage()
      this.filtersOpen = Storage.getValue('filters_open_crud_instances_monitor_system') == 'true'
      this.expandAll = Storage.getValue('expand_all_crud_instances_monitor_system') == 'true'
      this.quickFilter = Storage.getValue('quick_filter_crud_instances_monitor_system') || null
      var groupBy = Storage.getValue('group_by_crud_instances_monitor_system')
      if(groupBy) {
        try {
          var keys = JSON.parse(groupBy).filter(item => this.groupOptions.some(option => option.key == item))
          if(Array.isArray(keys)) this.groupBy = keys
        } catch (error) {
          this.groupBy = ['categorie']
        }
      }

      this.loadFieldTable()
      this.filterLoadSelects()
      this.readIdsFilter()
      this.pendingSelect = this.$route.params.select || null
      this.filter()
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      // se puede llegar desde una notificación estando ya en la vista
      '$route.query.ids'() {
        this.readIdsFilter()
        this.filter()
      },
      // la vista queda viva en su pestaña: una nueva navegación trae otro select
      '$route'(to) {
        if(to.name == 'InstancesStaffCrudInstancesMonitorSystem' && to.params.select) {
          this.pendingSelect = to.params.select
          this.selectFromRoute()
        }
      },
      filtersOpen(value) {
        Storage.setValue('filters_open_crud_instances_monitor_system', value ? 'true' : 'false')
      },
      expandAll(value) {
        Storage.setValue('expand_all_crud_instances_monitor_system', value ? 'true' : 'false')
      },
      groupBy(value) {
        Storage.setValue('group_by_crud_instances_monitor_system', JSON.stringify(value))
      },
      quickFilter(value) {
        Storage.setValue('quick_filter_crud_instances_monitor_system', value || '')
      },
    },
    computed: {
      // en mobile (tabla vertical) la columna del botón de opciones se titula "Acciones"
      tableFields() {
        return this.table.fields.map(field => field.key === 'f_action' ? Object.assign({}, field, { label: this.isMobileViewport ? 'Acciones' : '' }) : field)
      },
      // indicadores del listado (según los filtros aplicados)
      summaryTiles() {
        var items = this.table.items
        var count = fn => items.filter(fn).length
        return [
          { key: 'all', label: 'Instancias', value: items.length, icon: 'deployed_code', tone: 'neutral' },
          { key: 'ok', label: 'Reporte al día', value: count(item => reportState(item) == 'ok'), icon: 'check_circle', tone: 'success', filter: true },
          { key: 'attention', label: 'Atrasadas o sin reportes', value: count(item => ['stale', 'never'].indexOf(reportState(item)) >= 0), icon: 'schedule', tone: 'warning', filter: true },
          { key: 'avisos', label: 'Con avisos', value: count(item => countAvisos(item) > 0), icon: 'warning', tone: 'danger', filter: true },
          { key: 'controlBackups', label: 'Controlan backups', value: count(item => !!item.control_backups), icon: 'cloud_done', tone: 'info', filter: true },
          { key: 'backups', label: 'Backups no realizados', value: count(item => this.backupProblem(item)), icon: 'backup', tone: 'danger', filter: true },
          { key: 'inactive', label: 'Sistemas inactivos', value: count(item => this.isInactive(item)), icon: 'power_off', tone: 'danger', filter: true },
          { key: 'pending', label: 'Pendientes de emparejar', value: count(item => reportState(item) == 'pending'), icon: 'link_off', tone: 'info', filter: true },
        ]
      },
      visibleItems() {
        // desde una notificación: solo esos registros, sin otros filtros
        if(this.idsFilter.length) {
          return this.table.items.filter(item => this.idsFilter.indexOf(item.id) >= 0)
        }
        var quick = this.quickFilter
        if(!quick) return this.table.items
        return this.table.items.filter(item => {
          var state = reportState(item)
          if(quick == 'ok') return state == 'ok'
          if(quick == 'attention') return state == 'stale' || state == 'never'
          if(quick == 'pending') return state == 'pending'
          if(quick == 'avisos') return countAvisos(item) > 0
          if(quick == 'controlBackups') return !!item.control_backups
          if(quick == 'backups') return this.backupProblem(item)
          if(quick == 'inactive') return this.isInactive(item)
          return true
        })
      },
      // niveles disponibles del árbol
      groupOptions() {
        return [
          { key: 'categorie', label: 'Agrupación', icon: 'folder', emptyLabel: 'Sin agrupación', value: item => item.categorie },
          { key: 'customer', label: 'Cliente', icon: 'person', emptyLabel: 'Sin cliente', value: item => item.customer },
          { key: 'server', label: 'VPS', icon: 'dns', emptyLabel: 'Sin VPS asignada', value: item => item.server },
          { key: 'type', label: 'Tipo', icon: 'deployed_code', value: item => item.type ? { id: item.type, name: this.getTypeLabel(item.type), image: this.getTypeImage(item.type) } : null },
        ]
      },
      groupLevels() {
        return this.groupBy.map(key => this.groupOptions.find(item => item.key == key)).filter(Boolean)
      },
      // árbol según el agrupamiento elegido
      tree() {
        return buildTree(this.visibleItems, this.groupLevels)
      },
      // orden del listado tal cual se ve, para navegar en el panel lateral
      orderedItems() {
        return flattenTree(this.tree, this.visibleItems)
      },
      selectedIndex() {
        return this.selected ? this.orderedItems.findIndex(item => item.id == this.selected.id) : -1
      },
      canGoPrev() {
        return this.selectedIndex > 0
      },
      canGoNext() {
        return this.selectedIndex >= 0 && this.selectedIndex < this.orderedItems.length - 1
      },
      // CONDITIONS SELECT
      conditionCustomersActive() {
        return [{field: 'active', condition: true}]
      },
      conditionCategoriesSystem() {
        return [{field: 'type', condition: 'system'}]
      },
      conditionServicesCustomersDetails() {
        return [
          {field: 'customer_id', condition: this.crud.form.customer_id},
          {field: 'canceled', condition: false},
          {field: 'type_instances', condition: 'instance'},
          // sin los ya vinculados a otra instancia (el de esta instancia sigue apareciendo)
          {field: 'available_instance', condition: this.crud.form.id || 0},
        ]
      },
    },
    methods: {
      countAvisos,
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      loadFieldTable() {
        this.table.fields = [
          {key: 'name', label: 'Instancia', class: 'align-middle'},
          {key: 'customer', label: 'Cliente', class: 'align-middle'},
          ...(this.parameters.integrateServices ? [{key: 'service', label: 'Servicio', class: 'align-middle text-md-center'}] : []),
          {key: 'server', label: 'VPS', class: 'align-middle'},
          {key: 'status', label: 'Estado', class: 'align-middle'},
          {key: 'backups', label: 'Backups', class: 'align-middle'},
          {key: 'reported', label: 'Monitor', class: 'align-middle'},
          {key: 'avisos', label: 'Avisos', class: 'align-middle text-md-center'},
          {key: 'f_action', label: '', class: 'align-middle text-center'},
        ]
      },
      rowClass(item) {
        return item && this.selected && this.drawer.active && item.id == this.selected.id ? 'instances-monitor-table__row--selected' : 'instances-monitor-table__row'
      },

      toggleExpandAll() {
        this.expandAll = !this.expandAll
      },
      getGroupHealth(items) {
        var health = { avisos: 0, attention: 0, pending: 0, backups: 0 }
        items.forEach(item => {
          var state = reportState(item)
          if(countAvisos(item)) health.avisos++
          if(this.backupProblem(item)) health.backups++
          if(state == 'pending') health.pending++
          else if(state != 'ok') health.attention++
        })
        return health
      },
      getTypeLabel(type) {
        var found = SYSTEM_TYPES.find(item => item.value == type)
        return found ? found.text : type
      },
      getTypeImage(type) {
        var found = SYSTEM_TYPES.find(item => item.value == type)
        return found ? found.image : null
      },
      getTypeIcon(type) {
        var found = SYSTEM_TYPES.find(item => item.value == type)
        return found ? found.icon : 'deployed_code'
      },

      // estado reportado por Monitor
      // mismo criterio que el widget "INSTANCIAS sin backups": sin reporte,
      // sin ningún backup o con el próximo programado ya vencido
      backupProblem(item) {
        if(!item.control_backups || !item.monitor_id) return false
        var backups = parseJson(item.monitor_backups)
        if(!backups || typeof backups != 'object') return true
        if(!backups.ultimo_backup) return true
        var next = parseDate(backups.proximo_backup)
        return !!next && next.getTime() < Date.now()
      },
      // Monitor reporta backups de una instancia que no tiene activado el control
      unexpectedBackups(item) {
        if(item.control_backups) return false
        var backups = parseJson(item.monitor_backups)
        return !!backups && typeof backups == 'object' && !!backups.tamano
      },
      // inconsistencia entre la intranet configurada (Zyncro) y la que reporta Monitor.
      // Sólo se evalúa si Monitor ya reportó el estado
      intranetMismatch(item) {
        if(item.type != 'zyncro') return ''
        var status = parseJson(item.monitor_status)
        if(!status || typeof status != 'object' || !status.sistema) return ''
        var reported = !!status.intranet
        if(item.intranet && !reported) return 'La instancia tiene el sitio web activado pero Monitor no lo reporta'
        if(!item.intranet && reported) return 'Monitor reporta un sitio web pero la instancia no lo tiene activado'
        return ''
      },
      intranetMismatchShort(item) {
        return item.intranet ? 'Sitio web no reportado' : 'Sitio web no esperado'
      },
      // links de acceso tomados de los dominios que reporta Monitor: el primero,
      // o los dos primeros si es Zyncro con intranet (sistema e intranet)
      instanceLinks(item) {
        var status = parseJson(item.monitor_status)
        if(!status || typeof status != 'object' || !Array.isArray(status.dominios)) return []
        var total = item.type == 'zyncro' && status.intranet ? 2 : 1
        return status.dominios.filter(domain => domain && domain.dominio).slice(0, total).map(domain => {
          var host = String(domain.dominio).replace(/^https?:\/\//i, '').replace(/\/+$/, '')
          return { url: 'https://' + host, label: host.length > 32 ? host.substr(0, 30) + '…' : host }
        })
      },
      backupLabel(item) {
        if(!item.control_backups) return 'Sin control'
        if(!item.monitor_id) return 'Activado'
        var backups = parseJson(item.monitor_backups)
        if(!backups || typeof backups != 'object') return 'Sin reporte'
        if(!backups.ultimo_backup) return 'Ninguno todavía'
        return timeAgo(backups.ultimo_backup)
      },
      isInactive(item) {
        var status = parseJson(item.monitor_status)
        return !!(status && status.sistema && status.sistema.estado == 'inactivo')
      },

      // panel lateral
      openDrawer(item) {
        this.selected = item
        this.drawer.active = true
      },
      goToServer(server) {
        // por params (no query): la URL no cambia y el sistema de pestañas reutiliza la de Servidores
        this.$router.push({ name: 'InstancesStaffCrudInstancesMonitorServer', params: { select: String(server.id) } })
      },
      goPrev() {
        if(this.canGoPrev) this.openDrawer(this.orderedItems[this.selectedIndex - 1])
      },
      goNext() {
        if(this.canGoNext) this.openDrawer(this.orderedItems[this.selectedIndex + 1])
      },

      // crud
      // llegada desde Servidores (instancias del servidor): abre su rama y su detalle
      selectFromRoute() {
        var id = this.pendingSelect
        if(!id || this.table.isBusy || !this.table.items.length) return
        var item = this.table.items.find(system => String(system.id) == String(id))
        if(!item) {
          // los filtros guardados la excluyen: se limpian una vez y se vuelve a buscar
          var hasFilters = this.filters.name || this.filters.customer || this.filters.categories || this.filters.type || this.filters.instances_server
          if(hasFilters) {
            this.filters = { name: '', customer: null, categories: null, type: null, instances_server: null }
            this.filter()
          } else {
            this.pendingSelect = null
          }
          return
        }
        this.pendingSelect = null
        if(this.visibleItems.indexOf(item) < 0) {
          this.quickFilter = null
          this.idsFilter = []
        }
        this.revealId = item.id
        this.openDrawer(item)
      },
      readIdsFilter() {
        var ids = this.$route.query.ids
        this.idsFilter = ids ? String(ids).split(',').map(id => parseInt(id)).filter(id => !isNaN(id)) : []
      },
      clearIdsFilter() {
        this.idsFilter = []
        this.$router.replace({ query: {} })
      },
      filter() {
        // con ids de una notificación se trae todo, sin pisar los filtros guardados
        var params = this.idsFilter.length ? {} : this.filters
        if(!this.idsFilter.length) this.saveFilterStorage()
        this.table.isBusy = true

        var result = serviceAPI.filtrarMonitorSistema(params)

        result.then((response) => {
          this.table.items = response.data.data
          this.table.isBusy = false
          this.selectFromRoute()
          // refresca el panel abierto con los datos nuevos
          if(this.selected) {
            var fresh = this.table.items.find(item => item.id == this.selected.id)
            if(fresh) this.selected = fresh
            else this.drawer.active = false
          }
        })
        .catch(error => {
          this.table.isBusy = false
          this.$awn.alert(Error.showError(error))
        })
      },
      add() {
        this.crud.form.id = 0
        this.crud.form.name = ''
        this.crud.form.type = 'zyncro'
        this.crud.form.customer_id = 0
        this.crud.form.categories_id = 0
        this.crud.form.services_customers_details_id = 0
        this.crud.form.observations = ''
        this.crud.form.control_backups = true
        this.crud.form.observations_backups = ''
        this.crud.form.intranet = false
        this.crud.form.solucion = ''

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nueva Instancia"
        this.modal.form.active = true
      },
      edit(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name
        this.crud.form.type = item.type
        this.crud.form.customer_id = item.customer_id
        this.crud.form.categories_id = item.categories_id
        this.crud.form.services_customers_details_id = item.services_customers_details_id
        this.crud.form.observations = item.observations
        this.crud.form.control_backups = item.control_backups
        this.crud.form.observations_backups = item.observations_backups
        this.crud.form.intranet = item.intranet
        this.crud.form.solucion = item.solucion

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Editar Instancia"
        this.modal.form.active = true
      },
      save() {
        let loader = this.$loading.show()
        var result = this.crud.form.id
          ? serviceAPI.editarMonitorSistema(this.crud.form)
          : serviceAPI.agregarMonitorSistema(this.crud.form)

        result.then(() => {
          this.modal.form.active = false
          loader.hide()
          this.filter()
          this.$awn.success("Cambios guardado con éxito")
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      // solo pendientes de emparejar: una emparejada se desempareja desde Monitor
      remove(item) {
        this.confirmDialog('¿Desea eliminar la instancia (' + item.id + ') - ' + item.name + '?', {
          title: 'Eliminar Instancia',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if(!value) return

          let loader = this.$loading.show()
          serviceAPI.eliminarMonitorSistema(item.id)
            .then(() => {
              loader.hide()
              this.drawer.active = false
              this.filter()
              this.$awn.success("Registro eliminado")
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
        })
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },

      // SELECT
      loadCustomers(object) {
        this.crud.form.customer_id = object ? object.id : 0
      },
      loadCategories(object) {
        this.crud.form.categories_id = object ? object.id : 0
      },
      loadServicesCustomersDetails(object) {
        this.crud.form.services_customers_details_id = object ? object.id : 0
      },

      // filter select
      filterLoadSelects() {
        serviceAPI.obtenerClientes().then((response) => {
          var arrActive = []
          var arrInactive = []
          response.data.forEach(element => {
            if(element.active)
              arrActive.push({ code: element.id, label: element.name })
            else
              arrInactive.push({ code: element.id, label: '[INACTIVO] - ' + element.name })
          })
          this.arr.select.customers = arrActive.concat(arrInactive)
        })

        serviceAPI.obtenerCategoriesByType('system').then((response) => {
          this.arr.select.categories = response.data.map(element => ({code: element.id, label: element.name}))
        })

        serviceAPI.filtrarMonitorServidor({}).then((response) => {
          this.arr.select.servers = response.data.data.map(element => ({code: element.id, label: element.name}))
        })
      },

      // FILTROS SAVE
      saveFilterStorage() {
        Storage.setValue('filter_view_instances_monitor_system', JSON.stringify(this.filters))
      },
      restoreFilterStorage() {
        if(Storage.getValue('filter_view_instances_monitor_system')) {
          var saved = JSON.parse(Storage.getValue('filter_view_instances_monitor_system'))
          this.filters = Object.assign({}, this.filters, saved)
        }
        // el estado de Monitor se filtra ahora con los indicadores
        delete this.filters.reporting
      },
    }
  }
</script>

<style scoped>
  /* mobile (tabla vertical): el botón de opciones va a la izquierda, con la palabra "Opciones" */
  @media (max-width: 767.98px) {
    .instances-monitor-table >>> .b-table-stacked-md > tbody > tr > td:last-child {
      text-align: left !important;
    }
    .instances-monitor-table >>> .app-table-action-toggle {
      width: auto;
      gap: 4px;
      padding: 0 6px !important;
      border-radius: 6px;
      font-size: 14px;
    }
    .instances-monitor-table >>> .app-table-action-label {
      display: inline;
    }
  }

  .instances-monitor-form__hint {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 8px 10px;
    border-radius: 8px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary-active);
    font-size: 13px;
  }

  .instances-monitor-form__switch {
    padding: 10px 12px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
  }

  .instances-monitor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }

  .instances-monitor-table >>> .instances-monitor-table__row,
  .instances-monitor-table >>> .instances-monitor-table__row--selected {
    cursor: pointer;
  }

  .instances-monitor-table >>> .instances-monitor-table__row--selected {
    background: var(--app-color-primary-soft);
  }

  .instances-monitor__identity {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .instances-monitor__identity-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    max-width: 260px;
  }

  .instances-monitor__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 32px;
    height: 32px;
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text-secondary);
    font-size: 18px;
  }

  .instances-monitor__avatar--zyncro { background: var(--app-color-primary-soft); }
  .instances-monitor__avatar--laravel { background: #fff0ef; }
  .instances-monitor__avatar--docker { background: #e9f4fd; }

  .instances-monitor__service {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    font-size: 16px;
    cursor: default;
  }

  .instances-monitor__service--ok {
    background: var(--app-color-success-soft);
    color: var(--app-color-success);
  }

  .instances-monitor__service--missing {
    background: var(--app-color-danger-soft);
    color: var(--app-color-danger);
    box-shadow: 0 0 0 2px var(--app-color-danger);
  }

  .instances-monitor__link {
    color: var(--app-color-primary);
    font-weight: 500;
  }

  .instances-monitor__urls {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .instances-monitor__url {
    font-size: 11px;
    line-height: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--app-color-text-muted);
  }

  .instances-monitor__url:hover {
    color: var(--app-color-primary);
  }

  .instances-monitor__backup-alert {
    display: flex;
    width: max-content;
    align-items: center;
    gap: 3px;
    margin-bottom: 2px;
    padding: 0 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    white-space: nowrap;
    background: var(--app-color-warning-soft);
    color: var(--app-color-warning-hover);
  }

  .instances-monitor__link:hover {
    text-decoration: underline;
  }

  .instances-monitor__brand {
    width: 18px;
    height: 18px;
  }

  .instances-monitor__brand--sm {
    width: 13px;
    height: 13px;
  }

  /* el estado de Monitor ocupa toda la fila: el chip ("Atrasado · hace 2 d") no
     entra en un tercio del panel */
  .instances-monitor__stat--wide {
    grid-column: 1 / -1;
  }

</style>
